export const MIN_VOICEMAIL_ATTEMPT = 1;
export const MAX_VOICEMAIL_ATTEMPT = 99;

const ATTEMPTS_LIST_PATTERN = /^\d+(\s*,\s*\d+)*$/;

export interface VoicemailAttemptsParseResult {
  attempts: number[];
  error: string | null;
}

export const parseVoicemailOnAttemptsAsString = (value: string): VoicemailAttemptsParseResult => {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return {attempts: [], error: 'Enter at least one attempt number.'};
  }

  if (!ATTEMPTS_LIST_PATTERN.test(trimmed)) {
    return {
      attempts: [],
      error: 'Use a comma-separated list of whole numbers, for example 1, 2, 3.',
    };
  }

  const parsed = trimmed.split(',').map((part) => Number(part.trim()));

  const outOfRange = parsed.some(
    (attempt) => attempt < MIN_VOICEMAIL_ATTEMPT || attempt > MAX_VOICEMAIL_ATTEMPT,
  );
  if (outOfRange) {
    return {
      attempts: [],
      error: `Attempt numbers must be between ${MIN_VOICEMAIL_ATTEMPT} and ${MAX_VOICEMAIL_ATTEMPT}.`,
    };
  }

  if (new Set(parsed).size !== parsed.length) {
    return {attempts: [], error: 'Remove duplicate attempt numbers.'};
  }

  return {attempts: [...parsed].sort((a, b) => a - b), error: null};
};

/** Renders attempt numbers back into the field format, e.g. `[3, 1]` -> `'1, 3'`. */
export const formatVoicemailOnAttempts = (attempts: number[]): string =>
  [...attempts].sort((a, b) => a - b).join(', ');

const SINGLE_ATTEMPT_PATTERN = /^\d+$/;

export interface VoicemailAttemptParseResult {
  /** Parsed attempt number, or null when `error` is set. */
  attempt: number | null;
  /** User-facing message, or null when the input is valid. */
  error: string | null;
}

/** Parses a single attempt number, e.g. the attempt the follow-up template takes over after. */
export const parseVoicemailAttempt = (value: string): VoicemailAttemptParseResult => {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return {attempt: null, error: 'Enter an attempt number.'};
  }

  if (!SINGLE_ATTEMPT_PATTERN.test(trimmed)) {
    return {attempt: null, error: 'Enter a single whole number, for example 3.'};
  }

  const parsed = Number(trimmed);
  if (parsed < MIN_VOICEMAIL_ATTEMPT || parsed > MAX_VOICEMAIL_ATTEMPT) {
    return {
      attempt: null,
      error: `Attempt numbers must be between ${MIN_VOICEMAIL_ATTEMPT} and ${MAX_VOICEMAIL_ATTEMPT}.`,
    };
  }

  return {attempt: parsed, error: null};
};
